/* GENERATED FILE — DO NOT EDIT.
 *
 * Emitted from `contracts/openapi/rules.yaml` by `tools/openapi_ts/generate.py`.
 * Regenerate with `python -m tools.openapi_ts.generate`;
 * `test_generated_client_matches_the_contract` fails the build if this file drifts from the spec.
 *
 * Hard rule 10 makes the spec authoritative, so editing this file by hand would put the client and
 * the server behind two different sources of truth — which is the drift the rule exists to prevent.
 *
 * Each type has a matching `is<Name>` guard. The types are for `tsc`; the guards are because a
 * response is data from another process, and a compile-time type is only a claim about it.
 */


/**
 * Closed, because each kind implies a different execution strategy. Every kind is pushdown-only
 * (ADR-004 rung 1): the predicate is answered by SQL at the source and only counts come back. A
 * future kind needing data movement has to be added deliberately and reviewed.
 */
export type RuleKind = 'not_null' | 'unique' | 'accepted_values' | 'range' | 'pattern' | 'freshness' | 'row_count_anomaly' | 'referential_integrity' | 'custom_sql';

export const RULE_KINDS: readonly RuleKind[] = ['not_null', 'unique', 'accepted_values', 'range', 'pattern', 'freshness', 'row_count_anomaly', 'referential_integrity', 'custom_sql'];

export function isRuleKind(value: unknown): value is RuleKind {
  return (['not_null', 'unique', 'accepted_values', 'range', 'pattern', 'freshness', 'row_count_anomaly', 'referential_integrity', 'custom_sql'] as readonly unknown[]).includes(value);
}

/**
 * `deprecated` is not `deleted`. A deprecated rule takes on no new datasets and keeps every
 * violation it ever raised.
 */
export type RuleState = 'draft' | 'active' | 'deprecated';

export const RULE_STATES: readonly RuleState[] = ['draft', 'active', 'deprecated'];

export function isRuleState(value: unknown): value is RuleState {
  return (['draft', 'active', 'deprecated'] as readonly unknown[]).includes(value);
}

/**
 * A property of the *binding*, not of the version. The same expression is `info` on staging and
 * `blocker` on a regulatory return.
 */
export type Severity = 'info' | 'warning' | 'error' | 'blocker';

export const SEVERITYS: readonly Severity[] = ['info', 'warning', 'error', 'blocker'];

export function isSeverity(value: unknown): value is Severity {
  return (['info', 'warning', 'error', 'blocker'] as readonly unknown[]).includes(value);
}

/**
 * `partial` carries a fact nothing else can: the run finished and did not see everything. A score
 * derived from it must publish reduced coverage. `failed` and `cancelled` permit no score at all —
 * a number over datasets nobody checked is the product lying rather than the data being bad.
 */
export type ExecutionState = 'pending' | 'running' | 'succeeded' | 'partial' | 'failed' | 'cancelled';

export const EXECUTION_STATES: readonly ExecutionState[] = ['pending', 'running', 'succeeded', 'partial', 'failed', 'cancelled'];

export function isExecutionState(value: unknown): value is ExecutionState {
  return (['pending', 'running', 'succeeded', 'partial', 'failed', 'cancelled'] as readonly unknown[]).includes(value);
}

export type TaskState = 'pending' | 'running' | 'succeeded' | 'failed' | 'cancelled';

export const TASK_STATES: readonly TaskState[] = ['pending', 'running', 'succeeded', 'failed', 'cancelled'];

export function isTaskState(value: unknown): value is TaskState {
  return (['pending', 'running', 'succeeded', 'failed', 'cancelled'] as readonly unknown[]).includes(value);
}

/**
 * Part of the plan hash. A `sampled` run and a `full` run over the same datasets are not the same
 * work and their counts are not comparable, so they must hash differently.
 */
export type ExecutionStrategy = 'full' | 'sampled' | 'incremental';

export const EXECUTION_STRATEGYS: readonly ExecutionStrategy[] = ['full', 'sampled', 'incremental'];

export function isExecutionStrategy(value: unknown): value is ExecutionStrategy {
  return (['full', 'sampled', 'incremental'] as readonly unknown[]).includes(value);
}

export type SelectorKind = 'name_glob' | 'tag' | 'column_role';

export const SELECTOR_KINDS: readonly SelectorKind[] = ['name_glob', 'tag', 'column_role'];

export function isSelectorKind(value: unknown): value is SelectorKind {
  return (['name_glob', 'tag', 'column_role'] as readonly unknown[]).includes(value);
}

/**
 * Why there is nothing to show. `none_captured` means sampling was off, `expired` means the TTL
 * elapsed, `withheld` is a policy decision about columns classified PII. Conflating them tells an
 * analyst something false — an empty sample list for a 50-million-row violation reads as "clean".
 */
export type SampleState = 'available' | 'none_captured' | 'expired' | 'withheld';

export const SAMPLE_STATES: readonly SampleState[] = ['available', 'none_captured', 'expired', 'withheld'];

export function isSampleState(value: unknown): value is SampleState {
  return (['available', 'none_captured', 'expired', 'withheld'] as readonly unknown[]).includes(value);
}

/**
 * What a rule measures. Declared by the publisher rather than inferred, because `custom_sql` is
 * arbitrary SQL and nothing can tell what it checks — a guess there ends up inside a number
 * somebody defends to a regulator. The eight other kinds get a sensible default, so a client that
 * omits the field is not making a decision by accident. A `custom_sql` version that omits it is
 * refused with a `422`. `accuracy` is declarable and currently unmeasurable by any built-in kind:
 * it means "matches reality", which needs a reference source. A rule declaring it scores nothing
 * until such a rule exists, and `core.scoring` reports that as *no evidence* rather than as a
 * zero.
 */
export type QualityDimension = 'completeness' | 'validity' | 'accuracy' | 'consistency' | 'uniqueness' | 'timeliness' | 'integrity';

export const QUALITY_DIMENSIONS: readonly QualityDimension[] = ['completeness', 'validity', 'accuracy', 'consistency', 'uniqueness', 'timeliness', 'integrity'];

export function isQualityDimension(value: unknown): value is QualityDimension {
  return (['completeness', 'validity', 'accuracy', 'consistency', 'uniqueness', 'timeliness', 'integrity'] as readonly unknown[]).includes(value);
}

export interface Rule {
  readonly id: string;
  readonly name: string;
  readonly kind: RuleKind;
  /**
   * Required. An unowned rule is one nobody can be asked about when it fires at 03:00, which makes
   * its violations unactionable however correct they are.
   */
  readonly owner: string;
  readonly state: RuleState;
  readonly description: string | null;
  readonly tags: readonly string[];
}

export function isRule(value: unknown): value is Rule {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate['id'] === 'string' &&
    typeof candidate['name'] === 'string' &&
    (['not_null', 'unique', 'accepted_values', 'range', 'pattern', 'freshness', 'row_count_anomaly', 'referential_integrity', 'custom_sql'] as readonly unknown[]).includes(candidate['kind']) &&
    typeof candidate['owner'] === 'string' &&
    (['draft', 'active', 'deprecated'] as readonly unknown[]).includes(candidate['state']) &&
    (typeof candidate['description'] === 'string' || candidate['description'] === null) &&
    Array.isArray(candidate['tags']) &&
    (candidate['tags'] as readonly unknown[]).every((item) => typeof item === 'string')
  );
}

/**
 * A rule's editable metadata, complete. **`kind` and `state` are deliberately absent** — see
 * `updateRule`. `additionalProperties: false` is what makes that absence enforceable rather than
 * advisory: a client sending `kind` or `expression` is refused rather than silently having it
 * ignored, which is the difference between a surface that cannot change a version and one that
 * merely does not.
 */
export interface RuleUpdate {
  readonly name: string;
  readonly owner: string;
  /**
   * Nullable, and `null` means "clear it" rather than "leave it alone" — this is a full
   * replacement. Omitting the key entirely has the same effect, for the same reason.
   */
  readonly description?: string | null;
  /** The complete tag set. An empty array clears every tag; omitting the key does the same. */
  readonly tags?: readonly string[];
}

export function isRuleUpdate(value: unknown): value is RuleUpdate {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate['name'] === 'string' &&
    typeof candidate['owner'] === 'string'
  );
}

export interface TransitionApproval {
  readonly request_id: string;
  readonly rule_id: string;
  readonly transition: 'activate' | 'deprecate' | 'delete';
  /**
   * The token subject. Compared against every approver — the kernel refuses the requester's own
   * signature.
   */
  readonly requester: string;
  readonly required_eyes: number;
  /** Distinct approving humans so far. `1` of `2` is signed-once, not approved. */
  readonly eyes_collected: number;
  readonly satisfied: boolean;
  /**
   * `consumed` means the approved change was performed — **an approval is spent once** (migration
   * 0042). It is a distinct state rather than a flag because an auditor asks "was this approved"
   * and "was it used" separately, and collapsing them loses the second; this contract collapsed
   * them by omitting it. There is deliberately **no `expired`**, which this spec published for one
   * release. `ApprovalState` has no such member and says why: a timeout must never reach
   * `approved`, because "approve automatically if nobody responds in 24h" converts an approval
   * control into a delay that an auditor cannot tell apart from a real decision. An SLA breach is
   * `escalated`, which widens the approver pool and never substitutes for one.
   */
  readonly state: 'pending' | 'approved' | 'rejected' | 'escalated' | 'consumed';
  readonly submitted_at: string;
  readonly decisions: readonly Readonly<Record<string, unknown>>[];
}

export function isTransitionApproval(value: unknown): value is TransitionApproval {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate['request_id'] === 'string' &&
    typeof candidate['rule_id'] === 'string' &&
    (['activate', 'deprecate', 'delete'] as readonly unknown[]).includes(candidate['transition']) &&
    typeof candidate['requester'] === 'string' &&
    typeof candidate['required_eyes'] === 'number' &&
    typeof candidate['eyes_collected'] === 'number' &&
    typeof candidate['satisfied'] === 'boolean' &&
    (['pending', 'approved', 'rejected', 'escalated', 'consumed'] as readonly unknown[]).includes(candidate['state']) &&
    typeof candidate['submitted_at'] === 'string' &&
    Array.isArray(candidate['decisions'])
  );
}

export interface RuleExpression {
  readonly kind: RuleKind;
  /**
   * Named rather than inferred. A binding cannot be validated against a schema without knowing
   * which columns a rule reads, and hard rule 14 means a profile asks for named columns rather
   * than everything. May be empty **only** for `not_null` and `unique` — for those two kinds a
   * bare column list is the whole rule, so the same expression genuinely applies unchanged
   * wherever it is bound, and the column(s) may instead be named per binding ("Target Column") or
   * left unnamed there too, in which case planning checks every column the dataset actually has.
   * Every other kind is refused with a `422` for an empty list: `pattern`, `range` and
   * `accepted_values` each carry a parameter whose meaning is tied to the specific column it was
   * authored against, and `custom_sql`'s expression is dataset-wide and needs none.
   */
  readonly columns: readonly string[];
  /**
   * Kind-specific: `min`/`max` for range, `values` for accepted_values, `sql` for custom_sql.
   * Validated per kind before storage, because the alternative is a task that reaches a customer's
   * database and fails there — attributed to the source rather than to the rule.
   */
  readonly parameters: Readonly<Record<string, string>>;
}

export function isRuleExpression(value: unknown): value is RuleExpression {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    (['not_null', 'unique', 'accepted_values', 'range', 'pattern', 'freshness', 'row_count_anomaly', 'referential_integrity', 'custom_sql'] as readonly unknown[]).includes(candidate['kind']) &&
    Array.isArray(candidate['columns']) &&
    (candidate['columns'] as readonly unknown[]).every((item) => typeof item === 'string') &&
    candidate['parameters'] !== undefined
  );
}

export interface RuleVersion {
  readonly id: string;
  readonly rule_id: string;
  /**
   * major.minor.patch. No pre-release identifiers: a version either ran against production data or
   * it did not, and `1.2.0-rc1` invites the former while implying the latter.
   */
  readonly version: string;
  readonly expression: RuleExpression;
  readonly published_by: string;
  readonly published_at: string;
  /**
   * Always present in a response, even though it is optional in the request that created the
   * version. The server fills it from the kind or refuses, so a client never has to decide whether
   * an omission meant "the default" or "unknown".
   */
  readonly dimension: QualityDimension;
  readonly notes: string | null;
}

export function isRuleVersion(value: unknown): value is RuleVersion {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate['id'] === 'string' &&
    typeof candidate['rule_id'] === 'string' &&
    typeof candidate['version'] === 'string' &&
    candidate['expression'] !== undefined &&
    typeof candidate['published_by'] === 'string' &&
    typeof candidate['published_at'] === 'string' &&
    (['completeness', 'validity', 'accuracy', 'consistency', 'uniqueness', 'timeliness', 'integrity'] as readonly unknown[]).includes(candidate['dimension']) &&
    (typeof candidate['notes'] === 'string' || candidate['notes'] === null)
  );
}

export interface ExecutionScheduleWrite {
  /**
   * Omitted means false. A schedule can be stored disabled and switched on later, which is how a
   * cadence is agreed before anybody starts scanning a customer's rows.
   */
  readonly enabled?: boolean;
  /**
   * `daily` covers all seven days; `weekly` covers exactly the days named. There is deliberately
   * no hourly member — a rule execution scans rows — and no cron expression.
   */
  readonly cadence: 'daily' | 'weekly';
  /**
   * Wall-clock time in `timezone`, 24-hour `HH:MM`. Seconds and offset suffixes are refused: the
   * zone is a separate field, deliberately.
   */
  readonly local_time: string;
  /**
   * IANA zone name. **Never an offset** — an offset cannot express a DST transition, so it is
   * refused by the domain and by a CHECK constraint rather than misinterpreted.
   */
  readonly timezone: string;
  /**
   * Monday 0 through Sunday 6. Evaluated against the **local** date: a 01:00 Asia/Kolkata schedule
   * on a Monday is 19:30 UTC on the Sunday, so reading the weekday off the instant would run it on
   * the wrong day every week. Omitted means all seven, which `daily` requires.
   */
  readonly weekdays?: readonly number[];
  /**
   * **Required, with no default.** `full` scans every row of every resolved dataset; `sampled`
   * bounds the scan at the source. The two differ by orders of magnitude in what they read, so a
   * defaulted value would make the expensive promise silently. `incremental` is a real
   * `ExecutionStrategy` and is **not** schedulable: it renders identically to `full` today, so a
   * nightly schedule carrying it would scan whole tables while an operator believed it read only
   * new rows. Asking for it is a 422 that says so.
   */
  readonly strategy: 'full' | 'sampled';
  /**
   * Which violations the run files. A floor of `error` means fifty warning-level violations are
   * found and none recorded, so it is stored rather than implied — otherwise "why did last night
   * report nothing" is unanswerable.
   */
  readonly severity_floor?: 'blocker' | 'error' | 'warning' | 'info';
}

export function isExecutionScheduleWrite(value: unknown): value is ExecutionScheduleWrite {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    (['daily', 'weekly'] as readonly unknown[]).includes(candidate['cadence']) &&
    typeof candidate['local_time'] === 'string' &&
    typeof candidate['timezone'] === 'string' &&
    (['full', 'sampled'] as readonly unknown[]).includes(candidate['strategy'])
  );
}

export interface ExecutionSchedule {
  readonly binding_id: string;
  /**
   * False when nobody has set a schedule for this binding, in which case every field below is
   * absent and `detail` says so. Distinct from `enabled: false`, which is a decision somebody took
   * and can undo.
   */
  readonly configured: boolean;
  readonly enabled: boolean;
  /** Present only when `configured` is false. */
  readonly detail?: string;
  readonly cadence?: 'daily' | 'weekly';
  readonly local_time?: string;
  /**
   * Always returned beside `local_time`. `03:00` alone is the ambiguity this feature exists to
   * remove, so a client is never left to remember the zone.
   */
  readonly timezone?: string;
  readonly weekdays?: readonly number[];
  readonly strategy?: 'full' | 'sampled';
  readonly severity_floor?: 'blocker' | 'error' | 'warning' | 'info';
  /**
   * The schedule as a sentence, built by the platform, **including the strategy** — because the
   * recurrence alone does not say what a customer's database is in for. The parts above are what a
   * localised UI needs; this is the fallback and the thing an audit entry carries.
   */
  readonly describes?: string;
  /**
   * The next occurrence as a UTC instant, or null. Null for a **disabled** schedule — a future run
   * shown for something that will not happen is worse than showing nothing — and null for an
   * enabled one no sweep has primed yet. This is a *cache*, recomputed from `local_time` and
   * `timezone` on every claim. The pair is the configuration; if the two ever disagree, the pair
   * wins.
   */
  readonly next_run_at?: string | null;
  readonly last_run_at?: string | null;
  /**
   * Null when no occurrence has been reached — absence is not an outcome. `requested` means an
   * execution was dispatched, **not** that it succeeded: the execution row owns that. `deferred`
   * means an execution window was shut, which is the configuration working at an inconvenient
   * moment. `refused` means the platform declined — most often because the binding resolves to no
   * datasets, which is the commonest cause of "the rule is active and nothing is being checked"
   * and needs somebody to look at a selector. `failed` means the dispatch broke inside Lumiq,
   * which pages us rather than the customer. `skipped` means the binding was disabled between the
   * claim and the dispatch.
   */
  readonly last_outcome?: string | null;
  /**
   * One host-written sentence about `last_outcome`. Never a driver message: a driver error carries
   * the failing statement and with it the tenant's own literals.
   */
  readonly last_detail?: string | null;
  /**
   * True only for `refused` and `failed`. A deferral deliberately does not ask for attention — a
   * screen that highlighted every deferral would train somebody to ignore the highlight.
   */
  readonly needs_attention?: boolean;
  /**
   * True when the schedule has paused itself after consecutive refusals. Distinct from disabled: a
   * disabled schedule is somebody's decision, a suspension is a condition and it lapses on its
   * own.
   */
  readonly suspended?: boolean;
  readonly suspended_until?: string | null;
  readonly suspension_reason?: string | null;
  readonly configured_by?: string;
  readonly configured_at?: string;
}

export function isExecutionSchedule(value: unknown): value is ExecutionSchedule {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate['binding_id'] === 'string' &&
    typeof candidate['configured'] === 'boolean' &&
    typeof candidate['enabled'] === 'boolean'
  );
}

export interface ExecutionTriggerWrite {
  /**
   * Omitted means false. A trigger can be stored disabled and switched on later, which is how a
   * dependency is agreed before anybody starts scanning a customer's rows.
   */
  readonly enabled?: boolean;
  /**
   * The connection whose completed harvest fires this. Must name a connection in this tenant, and
   * must not equal the binding id — both are uuids in one body, so a client that swapped the
   * fields would otherwise store a trigger that can never fire.
   */
  readonly connection_id: string;
  /**
   * **Required, with no default**, for the reason a timed schedule gives: `full` and `sampled`
   * differ by orders of magnitude in what they read, so a defaulted value would make the expensive
   * promise silently. `incremental` is a real `ExecutionStrategy` and is refused with a 422 that
   * says so.
   */
  readonly strategy: 'full' | 'sampled';
  /**
   * Which violations the triggered run files. A floor of `error` means fifty warning-level
   * violations are found and none recorded, so it is stored rather than implied.
   */
  readonly severity_floor?: 'blocker' | 'error' | 'warning' | 'info';
}

export function isExecutionTriggerWrite(value: unknown): value is ExecutionTriggerWrite {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate['connection_id'] === 'string' &&
    (['full', 'sampled'] as readonly unknown[]).includes(candidate['strategy'])
  );
}

export interface ExecutionTrigger {
  readonly binding_id: string;
  /**
   * Absent when `configured` is false. Returned as an id rather than a name because this resource
   * has no connection name — resolving one is the catalog's, and a uuid inside `describes` would
   * make that sentence unreadable.
   */
  readonly connection_id?: string;
  /**
   * False when nobody has set a trigger for this binding, in which case every field below is
   * absent and `detail` says so. Distinct from `enabled: false`, which is a decision somebody took
   * and can undo.
   */
  readonly configured: boolean;
  readonly enabled: boolean;
  /** Present only when `configured` is false. */
  readonly detail?: string;
  readonly strategy?: 'full' | 'sampled';
  readonly severity_floor?: 'blocker' | 'error' | 'warning' | 'info';
  /**
   * The dependency as a sentence, built by the platform, **including the strategy** — because
   * "after each harvest" alone does not say what a customer's database is in for.
   */
  readonly describes?: string;
  /**
   * When a harvest completion last reached this trigger, **whatever the outcome**. Null when none
   * has. This is the "is this alive" field: a null on a connection that harvests nightly means
   * nothing is reaching the trigger, which is not the same as it having nothing to do.
   */
  readonly last_considered_at?: string | null;
  /**
   * When this trigger last actually caused an execution, and **what the cooldown is measured
   * from**. Separate from `last_considered_at` on purpose: if a skipped or deferred consideration
   * moved this, a harvest that failed would suppress the retry that fixed it.
   */
  readonly last_executed_at?: string | null;
  /**
   * Which harvest the last outcome was about. The causal link recorded rather than inferred from
   * two timestamps, which is the whole point of a declared dependency.
   */
  readonly last_harvest_id?: string | null;
  /**
   * Null when no harvest has reached this trigger — absence is not an outcome. `requested` means
   * an execution was dispatched, **not** that it succeeded: the execution row owns that.
   * `deferred` means the moment was wrong and nothing needs fixing — the cooldown had not lapsed,
   * the deployment's execution window was shut, or an execution of the same plan was already in
   * progress. `refused` means the platform declined, most often because the binding resolves to no
   * datasets even after a successful harvest, which needs somebody to look at a selector. `failed`
   * means the dispatch broke inside Lumiq, which pages us rather than the customer. `skipped`
   * means the harvest did not succeed, or the binding was disabled. **A deferral waits for the
   * next harvest, not for a next occurrence.** Unlike a timed schedule there is nothing still due;
   * the next harvest of the connection is the next attempt.
   */
  readonly last_outcome?: string | null;
  /**
   * One host-written sentence about `last_outcome`. Never a driver message: a driver error carries
   * the failing statement and with it the tenant's own literals.
   */
  readonly last_detail?: string | null;
  /**
   * True only for `refused` and `failed`. A deferral and a skip deliberately do not ask for
   * attention — a screen that highlighted every deferral would train somebody to ignore the
   * highlight.
   */
  readonly needs_attention?: boolean;
  /**
   * When the trigger may fire again, or null if nothing has executed yet. Returned so a deferral
   * can say *when* rather than only *that*.
   */
  readonly cools_until?: string | null;
  /**
   * True when the trigger has paused itself after consecutive refusals. Distinct from disabled: a
   * disabled trigger is somebody's decision, a suspension is a condition and it lapses on its own.
   */
  readonly suspended?: boolean;
  readonly suspended_until?: string | null;
  readonly suspension_reason?: string | null;
  readonly configured_by?: string;
  readonly configured_at?: string;
}

export function isExecutionTrigger(value: unknown): value is ExecutionTrigger {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate['binding_id'] === 'string' &&
    typeof candidate['configured'] === 'boolean' &&
    typeof candidate['enabled'] === 'boolean'
  );
}

/**
 * Both scheduling answers for one binding, as the per-binding routes would each return them.
 * `schedule` and `trigger` are always present and are the same objects those routes serve,
 * including the `configured: false` form. Making either optional would put a third state on the
 * wire — present, absent, or absent-because-unconfigured — that the single reads do not have, and
 * a client would have to handle a distinction the platform does not make.
 */
export interface SchedulingSummary {
  readonly binding_id: string;
  readonly schedule: ExecutionSchedule;
  readonly trigger: ExecutionTrigger;
}

export function isSchedulingSummary(value: unknown): value is SchedulingSummary {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate['binding_id'] === 'string' &&
    candidate['schedule'] !== undefined &&
    candidate['trigger'] !== undefined
  );
}

/**
 * No `meta`, deliberately. Every other listing here carries `PageMeta` because it is a window onto
 * something larger; this answers exactly the ids it was given, so there is no next page and a null
 * cursor beside it would only invite a client to look for one.
 */
export interface SchedulingSummaryList {
  readonly items: readonly SchedulingSummary[];
}

export function isSchedulingSummaryList(value: unknown): value is SchedulingSummaryList {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    Array.isArray(candidate['items']) &&
    (candidate['items'] as readonly unknown[]).every((item) => isSchedulingSummary(item))
  );
}

export interface Selector {
  readonly kind: SelectorKind;
  /**
   * Glob over `schema.table` — `*` and `?` only. Not a regex: a glob is what an operator can
   * predict, and a tenant-supplied regex is a pattern nobody on the team can reason about at
   * 03:00.
   */
  readonly pattern?: string | null;
  readonly tag?: string | null;
  readonly role?: string | null;
  /** The human form, e.g. `tagged pii`. Carried into audit records and onto screens. */
  readonly description: string;
}

export function isSelector(value: unknown): value is Selector {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    (['name_glob', 'tag', 'column_role'] as readonly unknown[]).includes(candidate['kind']) &&
    typeof candidate['description'] === 'string'
  );
}

export interface RuleBinding {
  readonly id: string;
  readonly rule_id: string;
  readonly rule_version_id: string;
  /**
   * A candidate matches when *any* selector matches — union, not intersection. Adding a second
   * selector broadens coverage, which is the commoner intent; intersection would silently produce
   * a much smaller resolved set than the author expected.
   */
  readonly selectors: readonly Selector[];
  readonly severity: Severity;
  readonly enabled: boolean;
  readonly created_by: string;
  readonly created_at: string;
  /**
   * The binding's own column list, independent of its selectors — the "Target Column" typed when
   * this binding was created. Applies to every dataset the binding resolves to, verbatim. Empty
   * means no column was named on the binding; for a `not_null` or `unique` rule published with no
   * columns either, planning then defaults to every column the dataset actually has.
   */
  readonly explicit_columns: readonly string[];
}

export function isRuleBinding(value: unknown): value is RuleBinding {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate['id'] === 'string' &&
    typeof candidate['rule_id'] === 'string' &&
    typeof candidate['rule_version_id'] === 'string' &&
    Array.isArray(candidate['selectors']) &&
    (candidate['selectors'] as readonly unknown[]).every((item) => isSelector(item)) &&
    (['info', 'warning', 'error', 'blocker'] as readonly unknown[]).includes(candidate['severity']) &&
    typeof candidate['enabled'] === 'boolean' &&
    typeof candidate['created_by'] === 'string' &&
    typeof candidate['created_at'] === 'string' &&
    Array.isArray(candidate['explicit_columns']) &&
    (candidate['explicit_columns'] as readonly unknown[]).every((item) => typeof item === 'string')
  );
}

export interface ResolvedTarget {
  readonly dataset_id: string;
  readonly qualified_name: string;
  /**
   * On the target, not only on the binding. A task *is* a rule version against a dataset, and
   * attributing tasks to "a" binding when two are bound to one table runs rule A's expression and
   * files the violation under rule B.
   */
  readonly rule_version_id: string;
  readonly columns: readonly string[];
  /**
   * Why this dataset was covered. "Because it is tagged `pii`" is answerable a year later; "the
   * selector matched" is not, and an auditor asking why coverage changed needs the former.
   */
  readonly matched_by: readonly string[];
}

export function isResolvedTarget(value: unknown): value is ResolvedTarget {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate['dataset_id'] === 'string' &&
    typeof candidate['qualified_name'] === 'string' &&
    typeof candidate['rule_version_id'] === 'string' &&
    Array.isArray(candidate['columns']) &&
    (candidate['columns'] as readonly unknown[]).every((item) => typeof item === 'string') &&
    Array.isArray(candidate['matched_by']) &&
    (candidate['matched_by'] as readonly unknown[]).every((item) => typeof item === 'string')
  );
}

export interface ExecutionProgress {
  readonly total: number;
  readonly succeeded: number;
  readonly failed: number;
  readonly cancelled: number;
}

export function isExecutionProgress(value: unknown): value is ExecutionProgress {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate['total'] === 'number' &&
    typeof candidate['succeeded'] === 'number' &&
    typeof candidate['failed'] === 'number' &&
    typeof candidate['cancelled'] === 'number'
  );
}

/**
 * A named schema rather than an inline object, and the reason is the generated client: an inline
 * object emits as `Readonly<Record<string, unknown>>`, so `description` — the only field a UI
 * actually renders — arrives as `unknown` and cannot be read without a cast. Naming the shape also
 * makes it documentable, which an anonymous one never is.
 */
export interface UnresolvedBinding {
  readonly binding_id: string;
  /** The selector in human form, e.g. `tagged pii`. The server's own wording. */
  readonly description: string;
}

export function isUnresolvedBinding(value: unknown): value is UnresolvedBinding {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate['binding_id'] === 'string' &&
    typeof candidate['description'] === 'string'
  );
}

export interface Plan {
  /**
   * `sha256:` prefixed, over the rule versions, dataset versions, scope, strategy and dialect. The
   * prefix is part of the value: when sha256 is replaced, every historical row still says which
   * recipe produced it, and a comparison across recipes can be refused rather than silently
   * reported as "changed".
   */
  readonly plan_hash: string;
  readonly strategy: ExecutionStrategy;
  readonly dialect: string;
  readonly dataset_count: number;
  readonly targets: readonly ResolvedTarget[];
  /**
   * Bindings that matched nothing. Reported rather than dropped: not an error, but the commonest
   * cause of "the rule is active and nothing is being checked".
   */
  readonly unresolved_bindings: readonly UnresolvedBinding[];
}

export function isPlan(value: unknown): value is Plan {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate['plan_hash'] === 'string' &&
    (['full', 'sampled', 'incremental'] as readonly unknown[]).includes(candidate['strategy']) &&
    typeof candidate['dialect'] === 'string' &&
    typeof candidate['dataset_count'] === 'number' &&
    Array.isArray(candidate['targets']) &&
    (candidate['targets'] as readonly unknown[]).every((item) => isResolvedTarget(item)) &&
    Array.isArray(candidate['unresolved_bindings']) &&
    (candidate['unresolved_bindings'] as readonly unknown[]).every((item) => isUnresolvedBinding(item))
  );
}

export interface Execution {
  readonly id: string;
  readonly state: ExecutionState;
  /**
   * Who asked for this run. Stored since executions existed and published here so a reader can
   * tell **why** a scan happened, which is not derivable from anything else on this object. Three
   * shapes, and the distinction is the point: a person's subject for a manual run;
   * `system:rule-scheduler` for a time-driven schedule; `system:harvest-follower` for a run that
   * followed a successful harvest. A client must treat any other `system:` value as an unattended
   * run of a kind it does not recognise rather than as a person — a new sweep is far likelier than
   * a colleague called `system:`.
   */
  readonly requested_by: string;
  readonly plan_hash: string;
  readonly strategy: ExecutionStrategy;
  readonly dialect: string;
  readonly severity_floor: Severity;
  readonly progress: ExecutionProgress;
  /**
   * The **resolved** dataset set, stored on the execution and never recomputed. A selector is a
   * question whose answer changes as tables are created, tagged and renamed; storing the question
   * would leave "why did this run cover 41 tables when the last covered 43" unanswerable, and a
   * score over an unknown set cannot be defended.
   */
  readonly targets: readonly ResolvedTarget[];
  readonly detail: string | null;
  readonly started_at: string | null;
  readonly finished_at: string | null;
  /**
   * Whether a score may be derived from this run at all. Exposed rather than left for a client to
   * infer from `state`, because inferring it is exactly where a consumer gets it wrong.
   */
  readonly may_publish_a_score: boolean;
}

export function isExecution(value: unknown): value is Execution {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate['id'] === 'string' &&
    (['pending', 'running', 'succeeded', 'partial', 'failed', 'cancelled'] as readonly unknown[]).includes(candidate['state']) &&
    typeof candidate['plan_hash'] === 'string' &&
    (['full', 'sampled', 'incremental'] as readonly unknown[]).includes(candidate['strategy']) &&
    typeof candidate['dialect'] === 'string' &&
    (['info', 'warning', 'error', 'blocker'] as readonly unknown[]).includes(candidate['severity_floor']) &&
    typeof candidate['requested_by'] === 'string' &&
    candidate['progress'] !== undefined &&
    Array.isArray(candidate['targets']) &&
    (candidate['targets'] as readonly unknown[]).every((item) => isResolvedTarget(item)) &&
    (typeof candidate['detail'] === 'string' || candidate['detail'] === null) &&
    (typeof candidate['started_at'] === 'string' || candidate['started_at'] === null) &&
    (typeof candidate['finished_at'] === 'string' || candidate['finished_at'] === null) &&
    typeof candidate['may_publish_a_score'] === 'boolean'
  );
}

export interface ExecutionFindings {
  readonly execution_id: string;
  /**
   * Violations across this run's **succeeded** tasks. `null` when none has succeeded yet — which
   * is a different fact from `0`, and the one this field exists to keep apart: a queued run must
   * never read as a clean one.
   */
  readonly violations: number | null;
  /**
   * Rows read across the same tasks, so a violation count has a denominator. `null` under the same
   * condition and for the same reason.
   */
  readonly rows_evaluated: number | null;
  /** Tasks that succeeded, i.e. how many tables the totals actually cover. */
  readonly measured_tasks: number;
  /**
   * Tasks the run planned. `0` for a run whose tasks have not been written yet, which is a real
   * state on a 202 route rather than a run covering nothing.
   */
  readonly total_tasks: number;
  /**
   * Whether the totals cover only some of the run's tables. Derived and published rather than left
   * to a client for `may_publish_a_score`'s reason — inferring it from the pair is exactly where a
   * consumer gets it wrong, and the same count over two of six tables and over all six are
   * different findings.
   */
  readonly partial: boolean;
}

export function isExecutionFindings(value: unknown): value is ExecutionFindings {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate['execution_id'] === 'string' &&
    (typeof candidate['violations'] === 'number' || candidate['violations'] === null) &&
    (typeof candidate['rows_evaluated'] === 'number' || candidate['rows_evaluated'] === null) &&
    typeof candidate['measured_tasks'] === 'number' &&
    typeof candidate['total_tasks'] === 'number' &&
    typeof candidate['partial'] === 'boolean'
  );
}

export interface Task {
  readonly id: string;
  readonly execution_id: string;
  readonly rule_version_id: string;
  readonly dataset_id: string;
  readonly qualified_name: string;
  readonly columns: readonly string[];
  readonly state: TaskState;
  readonly attempt: number;
  readonly violation_count: number;
  readonly rows_evaluated: number;
  /**
   * Null when nothing was examined — **not** 1.0. A table with no rows has no measured quality,
   * and reporting a perfect pass rate for it is how an empty pipeline output becomes a green
   * dashboard.
   */
  readonly pass_rate: number | null;
  readonly detail: string | null;
  readonly started_at: string | null;
  readonly finished_at: string | null;
}

export function isTask(value: unknown): value is Task {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate['id'] === 'string' &&
    typeof candidate['execution_id'] === 'string' &&
    typeof candidate['rule_version_id'] === 'string' &&
    typeof candidate['dataset_id'] === 'string' &&
    typeof candidate['qualified_name'] === 'string' &&
    Array.isArray(candidate['columns']) &&
    (candidate['columns'] as readonly unknown[]).every((item) => typeof item === 'string') &&
    (['pending', 'running', 'succeeded', 'failed', 'cancelled'] as readonly unknown[]).includes(candidate['state']) &&
    typeof candidate['attempt'] === 'number' &&
    typeof candidate['violation_count'] === 'number' &&
    typeof candidate['rows_evaluated'] === 'number' &&
    (typeof candidate['pass_rate'] === 'number' || candidate['pass_rate'] === null) &&
    (typeof candidate['detail'] === 'string' || candidate['detail'] === null) &&
    (typeof candidate['started_at'] === 'string' || candidate['started_at'] === null) &&
    (typeof candidate['finished_at'] === 'string' || candidate['finished_at'] === null)
  );
}

/**
 * Counts, and a reference. There is no field here a failing row could be assigned to, and that
 * absence is the design: putting sample rows in this response would put customer data — frequently
 * the exact PII the rule was checking for — into every client, log and cache that ever renders it.
 */
export interface Violation {
  readonly id: string;
  readonly execution_id: string;
  readonly task_id: string;
  readonly rule_id: string;
  readonly rule_version_id: string;
  readonly dataset_id: string;
  /**
   * Keyed on the version, not the dataset: a violation is a statement about a specific schema
   * state, and re-attributing it to a dataset whose columns have changed would let a finding about
   * a column that no longer exists look current.
   */
  readonly dataset_version_id: string;
  readonly severity: Severity;
  readonly violation_count: number;
  readonly rows_evaluated: number;
  readonly failure_rate: number;
  readonly columns: readonly string[];
  readonly sample_state: SampleState;
  /**
   * An object-store **key** for masked Parquet, never a URL and never the rows. A fetchable URL
   * would bypass the decrypt-and-mask step and the audit record that must accompany every sample
   * read.
   */
  readonly sample_ref: string | null;
  readonly detected_at: string;
}

export function isViolation(value: unknown): value is Violation {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate['id'] === 'string' &&
    typeof candidate['execution_id'] === 'string' &&
    typeof candidate['task_id'] === 'string' &&
    typeof candidate['rule_id'] === 'string' &&
    typeof candidate['rule_version_id'] === 'string' &&
    typeof candidate['dataset_id'] === 'string' &&
    typeof candidate['dataset_version_id'] === 'string' &&
    (['info', 'warning', 'error', 'blocker'] as readonly unknown[]).includes(candidate['severity']) &&
    typeof candidate['violation_count'] === 'number' &&
    typeof candidate['rows_evaluated'] === 'number' &&
    typeof candidate['failure_rate'] === 'number' &&
    Array.isArray(candidate['columns']) &&
    (candidate['columns'] as readonly unknown[]).every((item) => typeof item === 'string') &&
    (['available', 'none_captured', 'expired', 'withheld'] as readonly unknown[]).includes(candidate['sample_state']) &&
    (typeof candidate['sample_ref'] === 'string' || candidate['sample_ref'] === null) &&
    typeof candidate['detected_at'] === 'string'
  );
}

/**
 * The three customer-selectable modes for rung 4. Closed: a fourth is a change to what may cross
 * the wire, which is an ADR rather than an enum edit. `zero_row` is enforced by never building the
 * masking statement at all — the refusal precedes the read rather than discarding rows already
 * copied — and a violation of that tenant's reports `sample_state: withheld`, which is their
 * decision rather than our missing configuration.
 */
export type SampleMode = 'zero_row' | 'masked_sample' | 'customer_held';

export const SAMPLE_MODES: readonly SampleMode[] = ['zero_row', 'masked_sample', 'customer_held'];

export function isSampleMode(value: unknown): value is SampleMode {
  return (['zero_row', 'masked_sample', 'customer_held'] as readonly unknown[]).includes(value);
}

/**
 * One tenant's rung-4 answer. `declared: false` means nobody has chosen and the reported `mode` is
 * the platform default.
 */
export interface SamplePolicy {
  readonly declared: boolean;
  readonly mode: SampleMode;
  /**
   * How long a captured sample remains readable. A tenant may shorten this and nothing may
   * lengthen it. Enforced hourly by the `rules.expire_samples` sweep, which clears the reference
   * and destroys the object.
   */
  readonly retention_days: number;
  /**
   * Non-null only under `customer_held`, where it is a storage path inside a root this deployment
   * allows. Never a URL: a fetchable destination would let a client reach the bytes without the
   * decrypt-and-mask step or the audit record of the read.
   */
  readonly destination: string | null;
  readonly updated_at: string | null;
  /** Null exactly when `declared` is false — nobody set it. */
  readonly updated_by: string | null;
  /**
   * The **deployment's** answer, not the tenant's: false when no customer sample roots are
   * configured, in which case `customer_held` is refused with a 422. Reported here so a screen
   * does not offer a mode a submit will reject.
   */
  readonly customer_held_available: boolean;
}

export function isSamplePolicy(value: unknown): value is SamplePolicy {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate['declared'] === 'boolean' &&
    (['zero_row', 'masked_sample', 'customer_held'] as readonly unknown[]).includes(candidate['mode']) &&
    typeof candidate['retention_days'] === 'number' &&
    (typeof candidate['destination'] === 'string' || candidate['destination'] === null) &&
    (typeof candidate['updated_at'] === 'string' || candidate['updated_at'] === null) &&
    (typeof candidate['updated_by'] === 'string' || candidate['updated_by'] === null) &&
    typeof candidate['customer_held_available'] === 'boolean'
  );
}

/**
 * Masked rows, verbatim from the artifact. Every key in every row carries a `_digest` or
 * `_preview` suffix, because the masking projection at the source produced nothing else.
 */
export interface ViolationSample {
  readonly violation_id: string;
  /**
   * The *source* column names the masked aliases derive from, for a screen to label the rows.
   * Identifiers from the source's catalog — never values.
   */
  readonly columns: readonly string[];
  /**
   * At most `MAX_SAMPLE_ROWS` (100), capped **at the source** rather than trimmed here. An
   * investigator needs examples, not an export, and "download every failing row" is a
   * data-exfiltration feature wearing a debugging hat.
   */
  readonly rows: readonly (Readonly<Record<string, string | null>>)[];
  readonly row_count: number;
  /**
   * Carried with the rows deliberately: whoever has just been shown examples is the person who
   * needs to know how long they will be there, and a screen with no expiry invites an
   * investigation planned around evidence that will be gone.
   */
  readonly expires_at: string;
  /**
   * Names the per-tenant key that sealed the object — never the key. A sample encrypted under one
   * tenant's KEK is unreadable with another's even if the store's own isolation were
   * misconfigured, and a rotated KEK produces a refusal naming the rotation rather than a decrypt
   * failure that reads like corruption.
   */
  readonly kek_id: string;
}

export function isViolationSample(value: unknown): value is ViolationSample {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate['violation_id'] === 'string' &&
    Array.isArray(candidate['columns']) &&
    (candidate['columns'] as readonly unknown[]).every((item) => typeof item === 'string') &&
    Array.isArray(candidate['rows']) &&
    typeof candidate['row_count'] === 'number' &&
    typeof candidate['expires_at'] === 'string' &&
    typeof candidate['kek_id'] === 'string'
  );
}

export interface BulkRun {
  readonly run_id: string;
  readonly action: 'rules.binding.enable' | 'rules.binding.disable';
  /** What is being edited, in the kernel's vocabulary. */
  readonly aggregate_type: string;
  readonly item_count: number;
  readonly chunk_size: number;
  /**
   * `partial` is its own state rather than a flavour of `applied`, because a run that stopped at
   * chunk 7 of 40 has changed real rows and a reader must not have to open the journal to learn
   * that.
   */
  readonly state: 'pending' | 'previewing' | 'applying' | 'applied' | 'partial' | 'undoing' | 'undone' | 'failed';
  readonly requested_by: string;
  /**
   * Null until the apply finishes, because a run that has changed nothing has nothing to undo.
   * Rendered even when null so a reader can tell "no window yet" from "not reported".
   */
  readonly undo_expires_at?: string | null;
  /** True for `applied` and `partial`. Derived, never stored. */
  readonly undoable: boolean;
}

export function isBulkRun(value: unknown): value is BulkRun {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate['run_id'] === 'string' &&
    (['rules.binding.enable', 'rules.binding.disable'] as readonly unknown[]).includes(candidate['action']) &&
    typeof candidate['aggregate_type'] === 'string' &&
    typeof candidate['item_count'] === 'number' &&
    typeof candidate['chunk_size'] === 'number' &&
    (['pending', 'previewing', 'applying', 'applied', 'partial', 'undoing', 'undone', 'failed'] as readonly unknown[]).includes(candidate['state']) &&
    typeof candidate['requested_by'] === 'string' &&
    typeof candidate['undoable'] === 'boolean'
  );
}

export interface BulkReport {
  readonly run_id: string;
  readonly phase: 'apply' | 'undo';
  /** Every submitted id carries a verdict; this is how many there were. */
  readonly total_items: number;
  /**
   * One entry per status, **including the zeroes**. A payload carrying only what happened lets "0
   * applied of 400" and "400 applied" look alike.
   */
  readonly counts: Readonly<Record<string, unknown>>;
  readonly chunks: readonly Readonly<Record<string, unknown>>[];
}

export function isBulkReport(value: unknown): value is BulkReport {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate['run_id'] === 'string' &&
    (['apply', 'undo'] as readonly unknown[]).includes(candidate['phase']) &&
    typeof candidate['total_items'] === 'number' &&
    candidate['counts'] !== undefined &&
    Array.isArray(candidate['chunks'])
  );
}

/**
 * Violations over a window, with enough shape to act on. Every figure is derived from violation
 * rows the platform still holds, never from a stored total — so widening the window re-derives
 * history rather than leaving a row asserting a number nothing computes any more.
 */
export interface FailureWindow {
  /** Total violations detected in the window. Zero is a real answer and means nothing broke. */
  readonly violations: number;
  /** Distinct datasets those violations touched. */
  readonly datasets: number;
  /** Distinct rules that produced them — the denominator of the concentration claim. */
  readonly rules: number;
  /**
   * How many of the total the three worst rules account for. Never greater than `violations`; the
   * server refuses the combination rather than serving two figures counted over different windows.
   */
  readonly top_rule_violations: number;
  /**
   * Whether "a few rules account for most of this" is worth saying. **False when there are no more
   * rules than the top-N covers**, because then the top three *are* all of them and the sentence
   * is a tautology. On the wire rather than derived client-side, so two clients cannot disagree
   * about when the claim holds.
   */
  readonly concentrated: boolean;
  /** The period measured. Present so a reader is never comparing against an unstated window. */
  readonly window_hours: number;
}

export function isFailureWindow(value: unknown): value is FailureWindow {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate['violations'] === 'number' &&
    typeof candidate['datasets'] === 'number' &&
    typeof candidate['rules'] === 'number' &&
    typeof candidate['top_rule_violations'] === 'number' &&
    typeof candidate['concentrated'] === 'boolean' &&
    typeof candidate['window_hours'] === 'number'
  );
}
